
import React, { useState, useRef } from 'react';
import { Search, UserPlus, MoreHorizontal, CheckCircle, XCircle, FileSpreadsheet, Download, Loader2, Check, UserMinus, Users, X, Trash2, Edit2, MailCheck, Copy, ShieldCheck, UserCheck } from 'lucide-react';
import { Voter, User, UserRole } from '../types';

const OFFICIAL_ROLES = [
  UserRole.NATIONAL_DIRECTOR,
  UserRole.LOCAL_DIRECTOR,
  UserRole.NATIONAL_OFFICER,
  UserRole.LOCAL_OFFICER,
  UserRole.ASSISTANT
];

const Voters: React.FC<{ user: User }> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'delegates' | 'officials'>('delegates');
  const [voters, setVoters] = useState<Voter[]>(() => {
    const saved = localStorage.getItem('smsa_delegates');
    return saved ? JSON.parse(saved) : [];
  });
  const [search, setSearch] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [editingVoter, setEditingVoter] = useState<Voter | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  
  const [notification, setNotification] = useState<{ msg: string, type: 'info' | 'success' } | null>(null);
  const [lastCreatedCredentials, setLastCreatedCredentials] = useState<{ email: string, pass: string } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: UserRole.VOTER,
    weight: 1
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const isSystemAdmin = user.role === UserRole.SYSTEM_ADMIN;
  const isPresident = user.role === UserRole.PRESIDENT;
  const canManageRegistry = isSystemAdmin || isPresident;

  const filteredList = voters.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(search.toLowerCase()) || 
                         v.email.toLowerCase().includes(search.toLowerCase());
    
    if (activeTab === 'officials') {
      return matchesSearch && OFFICIAL_ROLES.includes(v.role);
    } else {
      return matchesSearch && v.role === UserRole.VOTER;
    }
  });

  const saveToStorage = (updated: Voter[]) => {
    setVoters(updated);
    localStorage.setItem('smsa_delegates', JSON.stringify(updated));
  };

  const showNotify = (msg: string, type: 'info' | 'success' = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManageRegistry) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setTimeout(() => {
      const bulkNewUsers: Voter[] = [
        { id: `b1-${Date.now()}`, name: 'Bulk Entry A', email: `entry.a.${Date.now()}@smsa-syria.org`, role: activeTab === 'officials' ? UserRole.LOCAL_OFFICER : UserRole.VOTER, weight: 1, hasVoted: false },
        { id: `b2-${Date.now()}`, name: 'Bulk Entry B', email: `entry.b.${Date.now()}@smsa-syria.org`, role: activeTab === 'officials' ? UserRole.LOCAL_OFFICER : UserRole.VOTER, weight: 1, hasVoted: false },
      ];
      saveToStorage([...voters, ...bulkNewUsers]);
      setIsUploading(false);
      setShowSuccess(true);
      showNotify(`Bulk import for ${activeTab} processed.`, 'success');
      setTimeout(() => setShowSuccess(false), 3000);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }, 1500);
  };

  const handleSubmitManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingVoter) {
      const updated = voters.map(v => v.id === editingVoter.id ? { ...v, ...formData } : v);
      saveToStorage(updated);
      showNotify(`Updated profile for ${formData.name}`);
      closeManualModal();
    } else {
      const newVoter: Voter = {
        id: 'v' + Date.now(),
        ...formData,
        hasVoted: false
      };
      
      const tempPass = Math.random().toString(36).slice(-8).toUpperCase();
      localStorage.setItem(`smsa_pass_${formData.email}`, tempPass);
      
      saveToStorage([...voters, newVoter]);
      setLastCreatedCredentials({ email: formData.email, pass: tempPass });
      showNotify(`Authorization Successful. Secret Generated.`, 'success');
    }
  };

  const deleteVoter = (id: string) => {
    const voter = voters.find(v => v.id === id);
    if (window.confirm(`Delete ${voter?.name}'s account permanently?`)) {
      saveToStorage(voters.filter(v => v.id !== id));
      localStorage.removeItem(`smsa_pass_${voter?.email}`);
      setActiveMenuId(null);
      showNotify(`Permanently removed ${voter?.name}`);
    }
  };

  const openEdit = (v: Voter) => {
    setEditingVoter(v);
    setFormData({ name: v.name, email: v.email, role: v.role, weight: v.weight });
    setIsManualModalOpen(true);
    setActiveMenuId(null);
  };

  const closeManualModal = () => {
    setIsManualModalOpen(false);
    setEditingVoter(null);
    setLastCreatedCredentials(null);
    setFormData({ 
      name: '', 
      email: '', 
      role: activeTab === 'officials' ? UserRole.LOCAL_OFFICER : UserRole.VOTER, 
      weight: 1 
    });
  };

  const clearRegistry = () => {
    const type = activeTab === 'officials' ? 'Officials' : 'Delegates';
    if (window.confirm(`CRITICAL: Wipe all members from the ${type} tab? This cannot be undone.`)) {
      const remainingVoters = voters.filter(v => {
        if (activeTab === 'officials') return !OFFICIAL_ROLES.includes(v.role);
        return v.role !== UserRole.VOTER;
      });
      saveToStorage(remainingVoters);
      showNotify(`${type} tab has been cleared.`, 'info');
    }
  };

  const downloadTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8,Full Name,Email,Role,Weight\nJohn Doe,john.doe@smsa-syria.org," + (activeTab === 'officials' ? 'LOCAL_OFFICER' : 'VOTER') + ",1";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `smsa_${activeTab}_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilteredAvailableRoles = () => {
    if (activeTab === 'delegates') return [UserRole.VOTER];
    return OFFICIAL_ROLES;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotify("Copied to clipboard", 'success');
  };

  const handleOpenManualAdd = () => {
    setFormData({
      ...formData,
      role: activeTab === 'officials' ? UserRole.LOCAL_OFFICER : UserRole.VOTER
    });
    setIsManualModalOpen(true);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto relative min-h-full pb-20">
      {notification && (
        <div className={`fixed top-8 right-8 z-[100] p-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right-8 duration-300 border ${
          notification.type === 'success' ? 'bg-emerald-600 text-white border-emerald-400' : 'bg-slate-800 text-white border-slate-600'
        }`}>
          {notification.type === 'success' ? <MailCheck size={20} /> : <CheckCircle size={20} />}
          <p className="font-bold text-sm">{notification.msg}</p>
          <button onClick={() => setNotification(null)} className="ml-2 opacity-50 hover:opacity-100"><X size={16} /></button>
        </div>
      )}

      <header className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 gap-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Member Registry</h1>
          <p className="text-slate-500 font-medium tracking-tight">Identity & Authority Management • SMSA-Syria</p>
        </div>
        
        {canManageRegistry && (
          <div className="flex flex-wrap gap-3">
             <button 
              onClick={handleOpenManualAdd}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-indigo-100 text-sm"
            >
              <UserPlus size={18} />
              Add {activeTab === 'officials' ? 'Official' : 'Delegate'}
            </button>
            <button 
              onClick={clearRegistry}
              className="px-5 py-2.5 rounded-xl border border-rose-100 bg-white text-rose-500 flex items-center gap-2 font-bold hover:bg-rose-50 transition-all text-sm"
            >
              <UserMinus size={18} />
              Purge Tab
            </button>
            <button 
              onClick={downloadTemplate}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 flex items-center gap-2 font-bold hover:bg-slate-50 transition-all text-sm"
            >
              <Download size={18} />
              CSV Template
            </button>
            
            <div className="relative">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv,.xlsx,.xls" className="hidden" />
              <button 
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
                className={`px-5 py-2.5 rounded-xl border-2 border-dashed flex items-center gap-2 font-bold transition-all text-sm ${
                  showSuccess ? 'bg-emerald-50 border-emerald-500 text-emerald-600' : 'bg-white border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {isUploading ? <Loader2 size={18} className="animate-spin" /> : showSuccess ? <Check size={18} /> : <FileSpreadsheet size={18} />}
                {isUploading ? 'Processing...' : 'Bulk Import'}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Tab Switcher */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit mb-8 shadow-inner">
        <button 
          onClick={() => setActiveTab('delegates')}
          className={`flex items-center gap-2 px-8 py-2.5 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'delegates' 
              ? 'bg-white text-indigo-600 shadow-md' 
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users size={18} />
          Delegates
        </button>
        <button 
          onClick={() => setActiveTab('officials')}
          className={`flex items-center gap-2 px-8 py-2.5 rounded-xl font-bold text-sm transition-all ${
            activeTab === 'officials' 
              ? 'bg-white text-indigo-600 shadow-md' 
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          <UserCheck size={18} />
          Officials
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px]">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-4 justify-between bg-slate-50/50">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder={`Search ${activeTab}...`} 
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm font-medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filteredList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Full Identity</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Designation</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Weight</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                  {canManageRegistry && <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredList.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50/50 transition-colors group font-medium text-sm">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center font-bold text-indigo-600 text-xs border border-indigo-100">
                          {v.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 leading-none mb-1">{v.name}</p>
                          <p className="text-[11px] text-slate-400">{v.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${
                        OFFICIAL_ROLES.includes(v.role) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {v.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-black text-slate-700">{v.weight}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-1.5 text-slate-300 font-bold text-[10px] uppercase">
                        <XCircle size={12} /> Offline
                      </span>
                    </td>
                    {canManageRegistry && (
                      <td className="px-6 py-4 text-right relative">
                        <button 
                          onClick={() => setActiveMenuId(activeMenuId === v.id ? null : v.id)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {activeMenuId === v.id && (
                          <div className="absolute right-6 top-12 w-32 bg-white rounded-xl shadow-xl border border-slate-100 z-20 py-1 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                            <button 
                              onClick={() => { openEdit(v); setActiveMenuId(null); }} 
                              className="px-4 py-2 text-left text-xs font-bold text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-2"
                            >
                              <Edit2 size={14} /> Edit
                            </button>
                            <button 
                              onClick={() => { deleteVoter(v.id); setActiveMenuId(null); }} 
                              className="px-4 py-2 text-left text-xs font-bold text-rose-500 hover:bg-rose-50 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 text-center flex flex-col items-center">
            <div className="bg-slate-50 p-8 rounded-full text-slate-200 mb-6">
              {activeTab === 'officials' ? <UserCheck size={64} /> : <Users size={64} />}
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Registry is Empty</h3>
            <p className="text-slate-500 max-w-sm font-medium mb-8">No members have been authorized for this category yet.</p>
            {canManageRegistry && (
              <button 
                onClick={handleOpenManualAdd}
                className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
              >
                Authorize New {activeTab === 'officials' ? 'Official' : 'Delegate'}
              </button>
            )}
          </div>
        )}
      </div>

      {isManualModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
            {lastCreatedCredentials ? (
              <div className="p-8 text-center space-y-6">
                <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-xl">
                  <ShieldCheck size={40} />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight mb-1">Account Initialized</h3>
                  <p className="text-slate-500 text-sm font-medium">The temporary portal secret is displayed below. Share this securely with the member.</p>
                </div>
                
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 space-y-4">
                  <div className="text-left">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Login Email</label>
                    <div className="flex items-center justify-between bg-white px-4 py-2.5 rounded-xl border border-slate-200">
                      <span className="font-bold text-slate-700 text-sm">{lastCreatedCredentials.email}</span>
                      <button onClick={() => copyToClipboard(lastCreatedCredentials.email)} className="text-indigo-600 hover:text-indigo-800 transition-colors"><Copy size={16} /></button>
                    </div>
                  </div>
                  <div className="text-left">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Temporary Portal Secret</label>
                    <div className="flex items-center justify-between bg-indigo-50 px-4 py-3 rounded-xl border border-indigo-100">
                      <span className="font-black text-indigo-700 tracking-widest text-lg">{lastCreatedCredentials.pass}</span>
                      <button onClick={() => copyToClipboard(lastCreatedCredentials.pass)} className="text-indigo-600 hover:text-indigo-800 transition-colors"><Copy size={20} /></button>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={closeManualModal}
                  className="w-full py-4 bg-slate-800 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-black transition-all"
                >
                  Close & Acknowledge
                </button>
              </div>
            ) : (
              <>
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                  <h3 className="font-black text-slate-800 uppercase tracking-tight">{editingVoter ? 'Update Profile' : `Add ${activeTab === 'officials' ? 'Official' : 'Delegate'}`}</h3>
                  <button onClick={closeManualModal} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmitManual} className="p-8 space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Full Member Name</label>
                    <input 
                      required
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g. Sarah Mansour" 
                      className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Authorized Email Address</label>
                    <input 
                      required
                      type="email" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="member@smsa-syria.org" 
                      className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Portal Role</label>
                      <select 
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                        className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                      >
                        {getFilteredAvailableRoles().map(r => (
                          <option key={r} value={r}>
                            {r.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Voting Weight</label>
                      <input 
                        type="number" 
                        min="1"
                        value={formData.weight}
                        onChange={(e) => setFormData({...formData, weight: parseInt(e.target.value) || 1})}
                        className="w-full px-4 py-3 bg-slate-100 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl transition-all outline-none font-bold text-slate-800"
                      />
                    </div>
                  </div>
                  <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                    {editingVoter ? 'Commit Changes' : 'Authorize & Generate Secret'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Voters;
